import { imageUtil } from '../../common/util/image-util'
import React from 'react'
import type { Group } from '../../types/groups'

interface GroupCardProps {
  group: Group
  onClick: (groupId: string) => void
  onJoin?: (groupId: string) => void
  isJoined?: boolean
  isLoading?: boolean
  showOfficialBadge?: boolean
}

export const GroupCard = ({
  group,
  onClick,
  onJoin,
  isJoined = false,
  isLoading = false,
  showOfficialBadge = false,
}: GroupCardProps): React.ReactElement => {
  const imageUrl = group.imageFileId ? imageUtil.getImageUrl(group.imageFileId) : undefined

  const handleJoinClick = (e: React.MouseEvent): void => {
    e.stopPropagation()
    if (onJoin && !isLoading) {
      onJoin(group.id)
    }
  }

  return (
    <div
      className="cursor-pointer bg-dark-gray rounded-lg overflow-hidden hover:opacity-90 transition-opacity"
      onClick={() => onClick(group.id)}
    >
      {imageUrl ? (
        <div className="aspect-video bg-gray-700 relative">
          <img src={imageUrl} alt={group.name} className="w-full h-full object-cover" />
          {showOfficialBadge && (
            <div className="absolute top-2 right-2 bg-gold text-black text-xs font-bold px-2 py-1 rounded">OFFICIAL</div>
          )}
        </div>
      ) : (
        <div className="aspect-video bg-gray-700 flex items-center justify-center relative">
          <span className="text-4xl text-gray-500">{group.name.charAt(0).toUpperCase()}</span>
          {showOfficialBadge && (
            <div className="absolute top-2 right-2 bg-gold text-black text-xs font-bold px-2 py-1 rounded">OFFICIAL</div>
          )}
        </div>
      )}
      <div className="p-4">
        <h3 className="text-ivory font-evogria text-xl mb-2">{group.name}</h3>
        {group.description && <p className="text-gray-400 text-sm mb-3 line-clamp-2">{group.description}</p>}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 text-sm text-gray-400">
            <span>{group.memberCount ?? 0} members</span>
            <span className="capitalize">{group.type}</span>
          </div>
          {onJoin && (
            <button
              onClick={handleJoinClick}
              disabled={isJoined || isLoading}
              className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-colors ${
                isJoined
                  ? 'bg-gray-600 text-gray-400 cursor-default'
                  : isLoading
                  ? 'bg-gold-dark text-black opacity-50 cursor-wait'
                  : 'bg-gold text-black hover:bg-gold-light'
              }`}
            >
              {isLoading ? 'JOINING...' : isJoined ? 'JOINED' : 'JOIN'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
